import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import { ActivatedRoute } from '@angular/router';
import { AppComponent } from 'src/app/app.component';
import { FormGroup } from '@angular/forms';
import * as DeviceVctActions from 'src/app/store/device-vct/device-vct.actions';
import { selectDeviceVctMassiveShared, selectDeviceVctMassiveSharing, selectVctPlantsList } from 'src/app/store/device-vct/device-vct.selector';
import { FeatureVct, ModuleVct, Plant, VctExportRow } from 'src/app/model/device-vct/device-vct-implant';
import { DevicesVctFeatures, DevicesVctGenericType, DevicesVctModules, ImportFileSharingSuccess } from 'src/app/conf/devices-vct';
import { exportToCsvWithInfo } from '../../shared/utils/exportData';
import { ShareDeviceVctUploadDialogComponent } from '../share-device-vct-upload-dialog/share-device-vct-upload.dialog.component';
import { MassiveVctInvitationsFeature, MassiveVctInvitationsModule, MassiveVctInvitationsPlant } from 'src/app/model/device-vct/device-vct-invitations';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Component({
	selector: 'app-devices-vct-list-edit-filters',
	templateUrl: './devices-vct-list-edit-filters.component.html',
	providers: [ConfirmationService, DialogService]
})

export class DeviceVctListEditFiltersComponent implements OnInit, OnDestroy {
	@Input() deviceFilters: FormGroup;
	@Input() errorCount: number;
	@Output() importDialogClosed = new EventEmitter<void>();

	plantsList$: Observable<Plant[] | undefined>;
	plantsListSub: Subscription | undefined;
	deviceVctMassiveSharing$: Observable<boolean | undefined>;
	deviceVctMassiveShared$: Observable<boolean | undefined>;
	deviceVctMassiveSharedSub: Subscription;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;
	plantsList: Plant[];
	ref: DynamicDialogRef | undefined;
	COLUMN_TITLE_MIN_WD = 647;

	ImportFileSharingSuccess = ImportFileSharingSuccess;
	importFileSuccess = ImportFileSharingSuccess.NOT_UPLOADED; // -1: not imported due to block errors, 0: not uploaded, 1: imported

	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;
	LG_WIDTH = StandardDesignParameters.LG_WIDTH;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		public dialogService: DialogService,
		public app: AppComponent
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		// Store selectors
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.plantsList$ = this.store.select(selectVctPlantsList);
		this.deviceVctMassiveSharing$ = this.store.select(selectDeviceVctMassiveSharing);
		this.deviceVctMassiveShared$ = this.store.select(selectDeviceVctMassiveShared);
	}

	subscribeAll(): void {
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.plantsListSub = this.plantsList$?.subscribe(plantsList => {
			this.plantsList = plantsList;
		});
		this.deviceVctMassiveSharedSub = this.deviceVctMassiveShared$
			.pipe(filter(result => !!result))
			.subscribe((result) => {
				if (result) {
					this.disableMaximumSharingSystem();
				}
			});
	}

	ngOnDestroy(): void {
		this.plantsListSub?.unsubscribe();
	}

	saveSharingSystem(): void {
		const plant = this.plantsList[0];
		const plantId = plant.Id;
		const plantSave = new MassiveVctInvitationsPlant();
		if (plant !== undefined && plant.Id) {
			for (const module of plant.Modules) {
				if (module.ModuleId === DevicesVctModules.VideoUnit) {
					const moduleSave = new MassiveVctInvitationsModule(module.OwnerEmail, module.Id);
					if (module.Features !== undefined && module.Features.length > 0) {
						for (const feature of module.Features) {
							if (feature.FeatureId === DevicesVctFeatures.App) {
								moduleSave.SitefeatureInvitations.push(new MassiveVctInvitationsFeature(feature.SipAccount.UserEmail, feature.Id));
							}
						}
					}
					plantSave.SitemoduleInvitations.push(moduleSave);
				}
			}
		}
		this.store.dispatch(DeviceVctActions.massiveVctInvitations({ plantId: plantId, plant: plantSave }));
	}

	exportSharingSystem(): void {
		this.exportToCsv();
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'export_vct_share_success', params: { page_title: 'List Device | VCT' } }));
		}, 2000);
	}

	importSharingSystem(): void {
		//this.store.dispatch(PermissionActions.resetPermissionTemplateForm());
		this.ref = this.dialogService.open(ShareDeviceVctUploadDialogComponent, {
			header: this.translate.instant('IMPORT_PLANT_SHARING'),
			width: '80vw',
			breakpoints: { '1220px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {}
		});
		// notifying to vct-list-edit we closed import dialog
		this.ref.onClose.subscribe((importFileSuccess) => {
			// Output notify for vct-list-edit component
			this.importDialogClosed.emit();
			// 
			this.importFileSuccess = importFileSuccess;
		});
	}

	disableMaximumSharingSystem(): void {
		this.store.dispatch(DeviceVctActions.getDevicesVctList());
		this.store.dispatch(DeviceVctActions.getVctInvitations());
		this.store.dispatch(DeviceVctActions.enableEditSystem({ editSystem: false }));
	}

	exportToCsv() {
		const data = this.prepareDataForLogs();
		const info = [];
		info.push(this.translate.instant('EXPORT_FILE_VCT_SHARE_INFO_1'));
		info.push(this.translate.instant('EXPORT_FILE_VCT_SHARE_INFO_2'));
		info.push(this.translate.instant('EXPORT_FILE_VCT_SHARE_INFO_3'));
		const map = new Map();
		map.set("Id", this.translate.instant('ROW_ID'));
		map.set("Type", this.translate.instant('TYPE'));
		map.set("Name", this.translate.instant('NAME'));
		map.set("OwnerEmail", this.translate.instant('OWNER_EMAIL'));
		exportToCsvWithInfo(data, 'exportData', map, info);
	}

	prepareDataForLogs(): VctExportRow[] {
		const list = [];
		list.push(this.prepareDataForLogPlant(this.plantsList[0]));
		for (const module of this.plantsList[0].Modules) {
			if (module.ModuleId === DevicesVctModules.VideoUnit) {
				list.push(this.prepareDataForLogModule(module));
			}
			if (module.Features !== undefined && module.Features.length > 0) {
				for (const feature of module.Features) {
					if (feature.FeatureId === DevicesVctFeatures.App) {
						list.push(this.prepareDataForLogApp(feature));
					}
				}
			}
			/*if (module.ModuleId === DevicesVctModules.Targa && this.checkNameFilter(module)) {
				targa.push({ label: 'NO_DEVICES', data: module, icon: "pi cm-plate", children: [], expanded: true });
			}*/
		}
		return list;
	}

	prepareDataForLogPlant(plant: Plant): VctExportRow {
		const vctExportRow = new VctExportRow();
		vctExportRow.Id = plant.Id.toString() + '_' + DevicesVctGenericType.PLANT;
		vctExportRow.Name = plant.Name;
		vctExportRow.Type = 'PLANT';
		vctExportRow.OwnerEmail = '##' + (plant.OwnerEmail ? plant.OwnerEmail : '') + '**';
		return vctExportRow;
	}

	prepareDataForLogModule(module: ModuleVct): VctExportRow {
		let name = module.Name;
		if (module.AliasName !== '') {
			name = module.AliasName;
		}
		const vctExportRow = new VctExportRow();
		vctExportRow.Id = module.Id.toString() + '_' + DevicesVctGenericType.VIDEOUNIT;
		vctExportRow.Name = name;
		vctExportRow.Type = 'UNITVCT';
		vctExportRow.OwnerEmail = '##' + (module.OwnerEmail ? module.OwnerEmail : '') + '**';
		return vctExportRow;
	}

	prepareDataForLogApp(app: FeatureVct): VctExportRow {
		let name = app.Name;
		if (app.AliasName !== '') {
			name = app.AliasName;
		}
		const vctExportRow = new VctExportRow();
		vctExportRow.Id = app.Id.toString() + '_' + DevicesVctGenericType.APP;
		vctExportRow.Name = name;
		vctExportRow.Type = 'APP';
		vctExportRow.OwnerEmail = '##' + (app.SipAccount.UserEmail ? app.SipAccount.UserEmail : '') + '**';
		return vctExportRow;
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	underMinWidthCol4(): boolean {
		return StandardPageLayout.underMinWidthCol4(this.screenWidth);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	colFilterResponsive(elemType: string): string {
		return StandardPageLayout.colFilterResponsive(this.screenWidth, elemType);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-23rem');
	}

	tabletUnderLgWidth(): boolean {
		return StandardPageLayout.tabletUnderLgWidth(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}
}
